import React, { useMemo, useState, useEffect } from "react";
import Icon from "../icons/Icon";
import styles from "./tag.module.scss";

type ChipSolidThemes = "gray500" | "brown500";
type ChipOutlineThemes = "gray100";
type ChipThemes<T> = T extends "solid"
  ? ChipSolidThemes
  : T extends "outline"
    ? ChipOutlineThemes
    : never;
type ChipTypes = "solid" | "outline";

interface IChipProps<T extends ChipTypes> {
  onClick?: () => void;
  children?: React.ReactNode;
  className?: string;
  type?: T;
  theme?: ChipThemes<T>;
  size?: "small" | "medium" | "large";
  preComponent?: React.ReactNode;
  postComponent?: React.ReactNode;
  closeable?: boolean;
  onClose?: () => void;
  fontFamily?: string;
}

const Chip = ({
  type = "solid",
  theme = "gray500",
  size = "medium",
  onClick,
  children,
  preComponent,
  postComponent,
  closeable,
  onClose,
  fontFamily,
}: IChipProps<ChipTypes>) => {
  const closeIconType = useMemo(() => {
    if (theme === "gray500" && type === "solid") {
      return 1;
    } else if (theme === "brown500" && type === "solid") {
      return 2;
    } else if (theme === "gray100" && type === "outline") {
      return 3;
    }
  }, [theme, type]);

  const styleTag = `
    circle {
        fill: ${closeIconType === 1 ? "#DEDEDE" : closeIconType === 2 ? "#DDD1C1" : "#DEDEDE"};
    }
    path{
        stroke: ${closeIconType === 1 ? "#505050" : closeIconType === 2 ? "#6C563A" : "#505050"};
        
    }
  `;

  const [isShow, setIsShow] = useState(true);
  const IconXcircleG900Memo = React.memo(({ styleTag }: { styleTag: string }) => (
    <Icon name="XcircleG900" size={18} styleTag={styleTag}></Icon>
  ));
  const IconXcircleG900 = useMemo(() => <IconXcircleG900Memo styleTag={styleTag} />, [styleTag]);

  return (
    isShow &&
    IconXcircleG900 && (
      <div
        className={`${styles["chip"]} ${styles[size]} ${styles[type]} ${styles[theme]}`}
        onClick={() => {
          onClick && onClick();
        }}
        style={{
          fontFamily: fontFamily || "inherit",
        }}
      >
        {preComponent && preComponent}
        {children}
        {closeable && (
          <button
            onClick={() => {
              setIsShow(false);
              onClose && onClose();
            }}
            className={styles["close"]}
          >
            {IconXcircleG900}
          </button>
        )}
        {postComponent && postComponent}
      </div>
    )
  );
};

export default Chip;
